import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Rocket, Loader2, AlertCircle, CheckCircle2,
  Package, DollarSign, Globe, Target,
} from 'lucide-react';
import { countries } from '../constants/countries';

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400";
const labelCls = "block text-xs mb-1 text-slate-600 dark:text-slate-300 font-medium";
const cardCls  = "rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5";

const bidStrategies = [
  { value: 'TARGET_CPA',           label: 'Target CPA — Meta de Custo por Aquisição', showTarget: true  },
  { value: 'MAXIMIZE_CONVERSIONS', label: 'Maximize Conversions — Maximizar Conversões', showTarget: false },
  { value: 'TARGET_ROAS',          label: 'Target ROAS — Meta de ROAS',                  showTarget: true  },
  { value: 'MAXIMIZE_CLICKS',      label: 'Maximize Clicks — Maximizar Cliques',          showTarget: false },
  { value: 'MANUAL_CPC',           label: 'Manual CPC — CPC Manual',                      showTarget: false },
];

// ─── Preview Card ─────────────────────────────────────────────────────────────

function PreviewItem({ label, children }) {
  return (
    <div className={cardCls}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">{label}</p>
      {children}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

const CreateCampaign = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [presells, setPresells] = useState([]);

  const [savedAccounts, setSavedAccounts] = useState([]);
  const [selectedGoogleAccount, setSelectedGoogleAccount] = useState(null);
  const [availableMccs, setAvailableMccs] = useState([]);
  const [selectedMcc, setSelectedMcc] = useState(null);
  const [availableSubAccounts, setAvailableSubAccounts] = useState([]);
  const [selectedSubAccount, setSelectedSubAccount] = useState(null);
  const [loadingMccSubAccounts, setLoadingMccSubAccounts] = useState(false);

  const [formData, setFormData] = useState({
    google_ads_accounts_id: '',
    customer_id: '',
    login_customer_id: '',
    pages_id: '',
    scripts_id: '',
    products_id: '',
    name: '',
    budget_amount: 50,
    target_cpa: 25,
    start_date: null,
    end_date: null,
    country: 'US',
    language: 'en',
    targeting_type: 'PRESENCE_OR_INTEREST',
    bid_strategy: 'TARGET_CPA',
    ad_group_cpc_bid: null,
  });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPresell, setSelectedPresell] = useState(null);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchProducts();
    fetchPresells();
    fetchSavedAccounts();
  }, []);

  // ── Auth helper ────────────────────────────────────────────────────────────

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('sobevenda_token')}` });

  // ── Fetch functions ────────────────────────────────────────────────────────

  const fetchProducts = async () => {
    try {
      const res = await fetch('https://api.sobevenda.com.br/api-proxy/products/list', { headers: authHeaders() });
      if (res.ok) { const data = await res.json(); setProducts(data.products || []); }
    } catch { /* silencioso */ }
  };

  const fetchPresells = async () => {
    try {
      const res = await fetch('https://api.sobevenda.com.br/api-proxy/sobepages/pages', { headers: authHeaders() });
      if (res.ok) { const data = await res.json(); setPresells(data.pages || data.presells || data || []); }
    } catch { /* silencioso */ }
  };

  const fetchSavedAccounts = async () => {
    try {
      const res = await fetch('https://api.sobevenda.com.br/api-proxy/affiliate/google-ads-accounts', { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSavedAccounts(data.accounts || []);
        if (data.accounts?.length === 1) handleSelectGoogleAccount(data.accounts[0]);
      }
    } catch { /* silencioso */ }
  };

  const handleSelectGoogleAccount = async (account) => {
    setSelectedGoogleAccount(account);
    setSelectedMcc(null); setSelectedSubAccount(null);
    setAvailableMccs([]); setAvailableSubAccounts([]);
    try {
      const res = await fetch(`https://api.sobevenda.com.br/api-proxy/google-ads/accounts-structure?google_ads_accounts_id=${account.id}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data.mccs?.length > 0) {
          setAvailableMccs(data.mccs);
          if (data.mccs.length === 1) handleSelectMcc(data.mccs[0], account);
        } else if (data.accounts?.length > 0) {
          const subs = data.accounts.map(a => ({ customer_id: a.customer_id, descriptiveName: a.descriptiveName }));
          setAvailableSubAccounts(subs);
          if (subs.length === 1) { 
  handleSelectSubAccount(subs[0], null, account);
}
        }
      }
    } catch { setError('Erro ao buscar estrutura da conta'); }
  };

  const handleSelectMcc = async (mcc, account = selectedGoogleAccount) => {
  setSelectedMcc(mcc); setSelectedSubAccount(null); setAvailableSubAccounts([]); setLoadingMccSubAccounts(true);
  try {
    const res = await fetch(`https://api.sobevenda.com.br/api-proxy/google-ads/campaigns-all-mcc?mcc_customer_id=${mcc.customer_id}`, { headers: authHeaders() });
    if (res.ok) {
      const data = await res.json();
      if (data.account_names) {
        const subs = Object.entries(data.account_names).map(([id, name]) => ({ customer_id: id, descriptiveName: name }));
        setAvailableSubAccounts(subs);
        if (subs.length === 1) handleSelectSubAccount(subs[0], mcc, account); // ← passa account
      }
    }
  } catch { setError('Erro ao buscar contas da MCC'); }
  finally { setLoadingMccSubAccounts(false); }
};

  const handleSelectSubAccount = (sub, mcc = selectedMcc, account = selectedGoogleAccount) => {
  setSelectedSubAccount(sub);
  setFormData(prev => ({ 
    ...prev, 
    google_ads_accounts_id: account?.id || prev.google_ads_accounts_id,
    customer_id: sub.customer_id, 
    login_customer_id: mcc?.customer_id || null 
  }));
};

  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === parseInt(productId));
    setSelectedProduct(product);
    if (product) {
      const name = `${product.name} - ${formData.country} - ${new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
      let pagesId = formData.pages_id;
      if (product.presell_id) { pagesId = product.presell_id; setSelectedPresell(presells.find(p => p.id === product.presell_id)); }
      setFormData(prev => ({ ...prev, products_id: productId, pages_id: pagesId, name }));
    }
  };

  const handlePresellChange = (presellId) => {
    const presell = presells.find(p => p.id === parseInt(presellId));
    setSelectedPresell(presell);
    setFormData(prev => ({ ...prev, pages_id: presellId, scripts_id: presell?.script_id || prev.scripts_id }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'country' && selectedProduct) {
        next.name = `${selectedProduct.name} - ${value} - ${new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
      }
      return next;
    });
  };

  const handleCreateCampaign = async () => {
    if (!formData.customer_id) { setError('Selecione uma conta de anúncios'); return; }
    if (!formData.products_id) { setError('Selecione um produto'); return; }
    if (!formData.pages_id)    { setError('Selecione uma presell'); return; }
    if (!formData.name.trim()) { setError('Nome da campanha é obrigatório'); return; }
    if (formData.budget_amount <= 0) { setError('Budget deve ser maior que zero'); return; }
    if (formData.bid_strategy === 'TARGET_CPA' && formData.target_cpa <= 0) { setError('Meta CPA deve ser maior que zero'); return; }

    setLoading(true); setError('');
    try {
      const payload = {
        google_ads_accounts_id: parseInt(formData.google_ads_accounts_id),
        customer_id: formData.customer_id,
        login_customer_id: formData.login_customer_id || null,
        pages_id: parseInt(formData.pages_id),
        products_id: parseInt(formData.products_id),
        scripts_id: formData.scripts_id ? parseInt(formData.scripts_id) : null,
        name: formData.name,
        budget_amount: parseFloat(formData.budget_amount),
        target_cpa: parseFloat(formData.target_cpa),
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        country: formData.country,
        language: formData.language,
        targeting_type: formData.targeting_type || 'PRESENCE_OR_INTEREST',
        bid_strategy: formData.bid_strategy,
        ad_group_cpc_bid: formData.ad_group_cpc_bid ? parseFloat(formData.ad_group_cpc_bid) : null,
      };

      const res = await fetch('https://api.sobevenda.com.br/api-proxy/campaigns/create-complete', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || data.message || 'Erro ao criar campanha');

      navigate('/campanhas', { state: { message: 'Campanha criada com sucesso no Google Ads! Acesse o Google Ads e despause para começar a rodar.' } });
    } catch (err) {
      let msg = err.message || 'Erro ao criar campanha. Tente novamente.';
      if (msg.includes('AUTHORIZATION_ERROR')) msg = 'Erro de autenticação. Reconecte sua conta Google Ads.';
      else if (msg.includes('QUOTA_EXCEEDED'))    msg = 'Limite de requisições excedido. Tente novamente em alguns minutos.';
      else if (msg.includes('INVALID_CUSTOMER_ID')) msg = 'Customer ID inválido. Verifique a conta selecionada.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const selectedStrategy = bidStrategies.find(s => s.value === formData.bid_strategy);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">

        {/* HEADER */}
        <div>
          <button onClick={() => navigate('/campanhas')}
            className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 mb-4 transition">
            <ArrowLeft className="w-4 h-4" /> Voltar para Campanhas
          </button>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Rocket className="w-6 h-6" /> Criar Nova Campanha
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">Configure e lance sua campanha no Google Ads.</p>
        </div>

        {/* Erro */}
        {error && (
          <div className="p-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-900/20 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {/* GRID FORM + PREVIEW */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* FORM — col-span-2 */}
          <div className="lg:col-span-2 space-y-4">

            {/* 1. Conta e Produto */}
            <div className={cardCls}>
              <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-4">1. Selecionar conta e produto</h2>

              <div className="space-y-4">
                {/* Conta Google Ads */}
                <div>
                  <label className={labelCls}>Conta Google Ads <span className="text-red-500">*</span></label>
                  <select className={inputCls} value={selectedGoogleAccount?.id || ''}
                    onChange={(e) => { const a = savedAccounts.find(a => a.id === parseInt(e.target.value)); if (a) handleSelectGoogleAccount(a); }}>
                    <option value="">Selecione uma conta</option>
                    {savedAccounts.map(a => <option key={a.id} value={a.id}>{a.email || a.account_name || `Conta ${a.id}`}</option>)}
                  </select>
                </div>

                {/* MCC */}
                {availableMccs.length > 0 && (
                  <div>
                    <label className={labelCls}>MCC (Manager Account) <span className="text-red-500">*</span></label>
                    <select className={inputCls} value={selectedMcc?.customer_id || ''} disabled={loadingMccSubAccounts}
                      onChange={(e) => { const m = availableMccs.find(m => m.customer_id === e.target.value); if (m) handleSelectMcc(m); }}>
                      <option value="">Selecione uma MCC</option>
                      {availableMccs.map(m => <option key={m.customer_id} value={m.customer_id}>{m.account_name} · {m.customer_id}</option>)}
                    </select>
                    {loadingMccSubAccounts && (
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-500">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando subcontas...
                      </div>
                    )}
                  </div>
                )}

                {/* Subconta */}
                {availableSubAccounts.length > 0 && selectedMcc && (
                  <div>
                    <label className={labelCls}>Conta de Anúncios <span className="text-red-500">*</span></label>
                    <select className={inputCls} value={selectedSubAccount?.customer_id || ''}
                      onChange={(e) => { const s = availableSubAccounts.find(a => a.customer_id === e.target.value); if (s) handleSelectSubAccount(s); }}>
                      <option value="">Selecione uma conta</option>
                      {availableSubAccounts.map(a => <option key={a.customer_id} value={a.customer_id}>{a.descriptiveName} · {a.customer_id}</option>)}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">Conta onde a campanha será criada.</p>
                  </div>
                )}

                {/* Indicador conta selecionada */}
                {selectedSubAccount && (
                  <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {selectedSubAccount.descriptiveName}
                  </div>
                )}

                {/* Produto */}
                <div>
                  <label className={labelCls}>Produto <span className="text-red-500">*</span></label>
                  <select className={inputCls} value={formData.products_id} onChange={(e) => handleProductChange(e.target.value)}>
                    <option value="">Selecione um produto</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.currency} {p.price})</option>)}
                  </select>
                </div>

                {/* Presell */}
                <div>
                  <label className={labelCls}>
                    Presell (página de destino) <span className="text-red-500">*</span>
                    {selectedProduct?.presell_id && <span className="ml-2 text-emerald-600 dark:text-emerald-400">· Auto-selecionada</span>}
                    {presells.length > 0 && <span className="ml-1 text-slate-400">({presells.length})</span>}
                  </label>
                  <select className={inputCls} value={formData.pages_id} onChange={(e) => handlePresellChange(e.target.value)} disabled={!!selectedProduct?.presell_id}>
                    <option value="">{presells.length === 0 ? 'Nenhuma presell encontrada' : 'Selecione uma presell'}</option>
                    {presells.map(p => <option key={p.id} value={p.id}>{p.name || p.title || `Presell ${p.id}`}</option>)}
                  </select>
                  {presells.length === 0 && <p className="text-xs text-red-500 mt-1">Nenhuma presell criada. Crie uma presell primeiro.</p>}
                </div>
              </div>
            </div>

            {/* 2. Configurar Campanha */}
            <div className={cardCls}>
              <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-4">2. Configurar campanha</h2>

              <div className="space-y-4">
                {/* Nome */}
                <div>
                  <label className={labelCls}>Nome da campanha <span className="text-red-500">*</span></label>
                  <input type="text" className={inputCls} value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)} placeholder="Ex: Produto - US " />
                </div>

                {/* Budget + CPC */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Orçamento diário (moeda da conta) <span className="text-red-500">*</span></label>
                    <input type="number" step="1" min="1" className={inputCls} value={formData.budget_amount}
                      onChange={(e) => handleChange('budget_amount', parseFloat(e.target.value) || 0)} />
                  </div>
                  <div>
                    <label className={labelCls}>CPC do Ad Group (moeda da conta) <span className="text-slate-400 font-normal">opcional</span></label>
                    <input type="number" step="0.1" min="0" className={inputCls} value={formData.ad_group_cpc_bid || ''}
                      onChange={(e) => handleChange('ad_group_cpc_bid', e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="Deixe vazio para automático" />
                    <p className="text-xs text-slate-500 mt-1">Vazio = Google controla automaticamente.</p>
                  </div>
                </div>

                {/* País + Idioma */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>País alvo <span className="text-red-500">*</span></label>
                    <select className={inputCls} value={formData.country}
                      onChange={(e) => {
                        const newCountry = e.target.value;
                        const selected = countries.find(c => c.code === newCountry);
                        setFormData(prev => {
                          const next = { ...prev, country: newCountry, language: selected?.lang || 'en' };
                          if (selectedProduct) next.name = `${selectedProduct.name} - ${newCountry} - ${new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
                          return next;
                        });
                      }}>
                      {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Idioma</label>
                    <input className={`${inputCls} font-mono opacity-60 cursor-not-allowed`} value={formData.language.toUpperCase()} readOnly />
                    <p className="text-xs text-slate-500 mt-1">Ajustado automaticamente pelo país.</p>
                  </div>
                </div>

                {/* Estratégia de Lance */}
                <div>
                  <label className={labelCls}>Estratégia de lance <span className="text-red-500">*</span></label>
                  <select className={inputCls} value={formData.bid_strategy} onChange={(e) => handleChange('bid_strategy', e.target.value)}>
                    {bidStrategies.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>

                {/* Target CPA */}
                {selectedStrategy?.showTarget && formData.bid_strategy === 'TARGET_CPA' && (
                  <div>
                    <label className={labelCls}>Meta CPA (moeda da conta) <span className="text-red-500">*</span></label>
                    <input type="number" step="0.01" min="0.01" className={inputCls} value={formData.target_cpa}
                      onChange={(e) => handleChange('target_cpa', parseFloat(e.target.value) || 0)} />
                    <p className="text-xs text-slate-500 mt-1">Valor alvo que você quer pagar por conversão.</p>
                  </div>
                )}

                {/* Target ROAS */}
                {selectedStrategy?.showTarget && formData.bid_strategy === 'TARGET_ROAS' && (
                  <div>
                    <label className={labelCls}>Meta ROAS <span className="text-red-500">*</span></label>
                    <input type="number" step="0.1" min="0.1" className={inputCls} value={formData.target_roas || 4.0}
                      onChange={(e) => handleChange('target_roas', parseFloat(e.target.value) || 0)} />
                    <p className="text-xs text-slate-500 mt-1">Retorno alvo sobre investimento (ex: 4.0 = R$4 para cada R$1 gasto).</p>
                  </div>
                )}
              </div>
            </div>

            {/* Botões */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => navigate('/campanhas')}
                className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm font-medium">
                Cancelar
              </button>
              <button onClick={handleCreateCampaign} disabled={loading}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                {loading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Criando...</>
                  : <><Rocket className="w-4 h-4" /> Criar Campanha</>}
              </button>
            </div>
          </div>

          {/* PREVIEW — col-span-1 */}
          <div className="lg:col-span-1">
            <div className={`${cardCls} sticky top-6 space-y-3`}>
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Preview</h3>

              {selectedProduct ? (
                <>
                  <PreviewItem label="Produto">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{selectedProduct.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{selectedProduct.currency} {selectedProduct.price}</p>
                  </PreviewItem>

                  {selectedPresell && (
                    <PreviewItem label="Presell">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{selectedPresell.name || selectedPresell.title}</p>
                    </PreviewItem>
                  )}

                  <PreviewItem label="Conteúdo">
                    <div className="space-y-1.5 text-xs">
                      {[
                        { label: 'Headlines', value: selectedProduct.headlines?.length || 0 },
                        { label: 'Descriptions', value: selectedProduct.descriptions?.length || 0 },
                        { label: 'Keywords', value: selectedProduct.keywords?.length || 0 },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex justify-between">
                          <span className="text-slate-500">{label}</span>
                          <span className="font-semibold text-slate-900 dark:text-slate-100">{value}</span>
                        </div>
                      ))}
                    </div>
                  </PreviewItem>

                  <PreviewItem label="Orçamento">
                    <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                      {formData.budget_amount}
                      <span className="text-sm font-normal text-slate-500 ml-1">/dia</span>
                    </p>
                  </PreviewItem>

                  <PreviewItem label="Alvo">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {countries.find(c => c.code === formData.country)?.name}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{formData.bid_strategy.replace(/_/g, ' ')}</p>
                  </PreviewItem>
                </>
              ) : (
                <div className="text-center py-10">
                  <Package className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Selecione um produto para ver o preview.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCampaign;